import { createAsyncThunk } from '@reduxjs/toolkit';
import { CONSTANTS } from '../../../Helpers/Configuration';
import { filterCurriculumValues } from '../../../Helpers/CurriculumHelpers';
import { setValue } from '@nsr/react-form/dist/components/redux/slices/formSlice';
import { setCode, setCurriculumRules, setCurriculumValues } from '../slices/applicationSlice';
import get from 'lodash/get';
import { setSettingsValue } from '../slices/settingsSlice';
import { api } from '../../../ApiContext/ApiContext';
import { batch } from 'react-redux';

const { curriculumCachedQuestions } = CONSTANTS;
const childQuestions = ['major', 'minor', 'concentration'];

const getTemplate = (state) => {
    let template = state.applicationTemplate;

    if (!template?.id) {
        template = state.template;
    }

    return template;
};

const validAutosaveStatuses = [
    CONSTANTS.autosaveStatuses.SAVED,
    CONSTANTS.autosaveStatuses.INITIAL
];

export const autosaveAction = createAsyncThunk(
    'application/autosave',
    async (arg, thunkApi) => {
        const { dispatch, rejectWithValue } = thunkApi;
        const { application, settings } = thunkApi.getState();
        const { values } = arg;
        const newApplication = Object.assign({}, application, {
            values,
            revision: settings?.revision
        });

        // Ensure another autosave is not in flight
        if (validAutosaveStatuses.includes(settings?.autosave?.status)) {
            dispatch(setSettingsValue({
                name: 'autosave.status',
                value: CONSTANTS.autosaveStatuses.SAVING,
            }));

            try {
                const result = await api.applicationForms.autosave(newApplication);
                batch(() => {
                    dispatch(setSettingsValue({
                        name: 'autosave.status',
                        value: CONSTANTS.autosaveStatuses.SAVED,
                    }));
                    dispatch(setSettingsValue({
                        name: 'revision',
                        value: result.revision
                    }));
                });

                return result;
            } catch (err) {
                dispatch(setSettingsValue({
                    name: 'autosave.status',
                    value: CONSTANTS.autosaveStatuses.ERROR,
                }));

                rejectWithValue(err);
            }
        }
    }
);

export const fetchCurriculum = createAsyncThunk(
    'application/fetchCurriculum',
    async (arg, thunkApi) => {
        const state = thunkApi.getState();
        const template = arg.template || getTemplate(state);
        
        if (template?.settings?.curriculumFilteringEnabled) {
            const { changedQuestion, changedValue, api } = arg;
            const { dispatch } = thunkApi;
            const selectedValues = {
                [changedQuestion]: changedValue
            };
            const isMajorDescription = changedQuestion === 'majorDescription';
            let curriculumRules;
            let curriculumValues;

            if (isMajorDescription) {
                selectedValues.major = changedValue.major;
                selectedValues.program = changedValue.program;
            } else if (changedQuestion && changedValue) {
                selectedValues[changedQuestion] = changedValue;
            }

            template?.cachedQuestions?.forEach(q => {
                if (q.key !== changedQuestion && curriculumCachedQuestions.some(
                    cq => cq.key === q.key
                )) {
                    const value = get(state.application.codes, q.questionPath);

                    if (value) {
                        selectedValues[q.key] = value;
                    }
                }
            });

            // If the term didn't change and we aren't doing an initial fetch,
            // No reason to refetch rules.
            if (!changedQuestion || changedQuestion === 'term') {
                curriculumRules = await api.applicationForms.getCurriculumRules(selectedValues.term);
            } else {
                curriculumRules = state.application.curriculumRules;
            }

            curriculumValues = filterCurriculumValues(selectedValues, curriculumRules);

            if (!curriculumValues.valid) {
                const childQuestionChanged = childQuestions.includes(changedQuestion);

                curriculumCachedQuestions.forEach(q => {
                    const templateQuestion = template?.cachedQuestions?.find(
                        tq => tq.key === q.key
                    );
                    const isChangedQuestion = q.key === changedQuestion || (
                        isMajorDescription && (
                            q.key === 'program' || q.key === 'major'
                        )
                    );

                    // Clear everything but the term, level, and the question that just changed
                    // If a child question changed, don't clear the program.
                    if (
                        templateQuestion && q.key !== 'term' &&
                        q.key !== 'level' && !isChangedQuestion &&
                        (!childQuestionChanged || q.key !== 'program')
                    ) {
                        dispatch(setValue({
                            name: templateQuestion.questionPath,
                            value: null
                        }));
                        dispatch(setCode({
                            path: templateQuestion.questionPath,
                            code: null
                        }));
                        delete selectedValues[q.key];
                    }
                });

                curriculumValues = filterCurriculumValues(selectedValues, curriculumRules);
            } 

            dispatch(setCurriculumRules(curriculumRules));
            dispatch(setCurriculumValues(curriculumValues));
        }
    }
);
